import React, { useState } from 'react';
import { Alert, Button, Card, Popconfirm, Space, Table, Tag, Typography } from 'antd';
import { DownloadOutlined, HeartOutlined, PlayCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import firebase from 'firebase/app';
import moment from 'moment';

import { downloadCsv } from '../../utils/csv';

const { Text } = Typography;

const GET_REPORT = 'userService-healthHandler-adminGetCredentialHealthReport';
const RUN_SCAN = 'userService-healthHandler-adminRunCredentialHealthScan';

/**
 * Credential health: live-client owners with NO auth user — people locked out
 * (or one sign-in away from it) the way the first restored team owner was.
 *
 * The weekly cron (Mondays 07:00 Dublin) and the "Run scan now" button execute
 * the SAME scan and both refresh ops/credential-health, so the stored report
 * and an on-demand run never diverge. Rows resolve through the restore panel
 * on each user's admin page — a fixed row simply vanishes from the next run.
 */
export function CredentialHealthCard() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);

  const loadStored = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await firebase.functions().httpsCallable(GET_REPORT)();
      setReport(result.data || { neverRun: true });
    } catch (err) {
      setError(err.message || 'Could not load the report');
    } finally {
      setLoading(false);
    }
  };

  const runNow = async () => {
    setScanning(true);
    setError(null);
    try {
      const result = await firebase.functions().httpsCallable(RUN_SCAN)();
      setReport(result.data);
    } catch (err) {
      setError(err.message || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const lastRun = report && report.lastRunAt && report.lastRunAt._seconds
    ? moment(report.lastRunAt._seconds * 1000).format('YYYY-MM-DD HH:mm')
    : null;
  // Rows live in the ops/credential-health/orphans SUBCOLLECTION (keyed on uid);
  // responses carry the summary counts plus a page of docs, and "Load more"
  // pages by last uid. The true count always comes from the summary.
  const orphaned = (report && report.orphans) || [];
  const totalOrphaned = (report && report.totalOrphaned != null)
    ? report.totalOrphaned : orphaned.length;

  const loadMore = async () => {
    setLoading(true);
    setError(null);
    try {
      const startAfterUid = orphaned.length ? orphaned[orphaned.length - 1].uid : undefined;
      const result = await firebase.functions().httpsCallable(GET_REPORT)({ startAfterUid });
      const nextPage = (result.data && result.data.orphans) || [];
      setReport({ ...report, ...result.data, orphans: [...orphaned, ...nextPage] });
    } catch (err) {
      setError(err.message || 'Could not load more');
    } finally {
      setLoading(false);
    }
  };

  const exportOrphansCsv = () => {
    const rows = [
      ['uid', 'email', 'signInDeletionRecorded', 'liveClients'],
      ...orphaned.map((row) => [
        row.uid,
        row.email || '',
        !!row.tombstoned,
        (row.clients || []).map((c) => `${c.clientId} ${c.displayName || ''}`.trim()).join('; '),
      ]),
    ];
    downloadCsv(`credential-health-${moment().format('YYYYMMDD-HHmmss')}.csv`, rows);
  };

  const columns = [
    {
      title: 'User (uid)',
      dataIndex: 'uid',
      key: 'uid',
      render: (uid) => <a href={`/admin/users/${uid}`}><Text code>{uid}</Text></a>,
    },
    { title: 'Email', dataIndex: 'email', key: 'email', render: (v) => v || <Text type="secondary">—</Text> },
    {
      title: 'Sign-in',
      dataIndex: 'tombstoned',
      key: 'tombstoned',
      render: (v) => (v
        ? <Tag color="red">deletion recorded — restorable</Tag>
        : <Tag color="orange">missing, deletion never recorded</Tag>),
    },
    {
      title: 'Live clients',
      dataIndex: 'clients',
      key: 'clients',
      render: (clients) => (clients || []).map((c) => (
        <a key={c.clientId} href={`/admin/clients/${c.clientId}`}>
          <Tag>{c.displayName || c.clientId}</Tag>
        </a>
      )),
    },
  ];

  return (
    <Card
      size="small"
      style={{ textAlign: 'left' }}
      data-testid="credential-health-card"
      title={<Text strong><HeartOutlined /> Credential health</Text>}
      extra={(
        <Space>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={loadStored}
            data-testid="credential-health-load"
          >
            Last report
          </Button>
          <Popconfirm
            title={(
              <div style={{ maxWidth: 320 }}>
                Scans <Text strong>every live client</Text> (tens of thousands of reads on
                production) and refreshes ops/credential-health. Read-only apart from that
                report doc. Takes about a minute. Obeys the same switch as the weekly
                cron — if it&apos;s OFF in Ops → Crons (the current default, pending the
                worklist rewrite), this refuses rather than scanning.
              </div>
            )}
            okText="Run scan"
            cancelText="Cancel"
            onConfirm={runNow}
          >
            <Button
              size="small"
              icon={<PlayCircleOutlined />}
              loading={scanning}
              data-testid="credential-health-run"
            >
              Run scan now
            </Button>
          </Popconfirm>
        </Space>
      )}
    >
      {!report && !error && (
        <Text type="secondary">
          Live-client owners with no auth credential (lockouts). Weekly cron writes
          ops/credential-health; load the last report or run the scan now.
        </Text>
      )}
      {report && report.neverRun && (
        <Text type="secondary">No report yet — the cron hasn&apos;t run. Use &quot;Run scan now&quot;.</Text>
      )}
      {report && !report.neverRun && (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Space wrap>
            {totalOrphaned === 0
              ? <Tag color="green">0 orphaned — clean</Tag>
              : <Tag color="red" data-testid="credential-health-count">{totalOrphaned} orphaned</Tag>}
            <Text type="secondary">
              {report.scannedOwners} owners across {report.scannedClients} live clients
              {lastRun ? ` · last run ${lastRun}` : ''}
              {report.truncated ? ' · TRUNCATED — raise the limit' : ''}
            </Text>
            {orphaned.length > 0 && (
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={exportOrphansCsv}
                data-testid="credential-health-export"
              >
                Export CSV
              </Button>
            )}
          </Space>
          {totalOrphaned > orphaned.length && (
            <Alert
              type="warning"
              showIcon
              message={`${totalOrphaned} affected people — ${orphaned.length} loaded.`}
              description="At this scale, work the list as a dataset: load more or export the CSV, resolve in batches (restore per user, or the attribution backfill), and re-run the scan — resolved rows drop out on the next run."
              style={{ borderRadius: 8 }}
              data-testid="credential-health-capped"
            />
          )}
          {orphaned.length > 0 && (
            <Table
              rowKey="uid"
              size="small"
              pagination={{ pageSize: 20, hideOnSinglePage: true, showSizeChanger: false }}
              columns={columns}
              dataSource={orphaned}
            />
          )}
          {totalOrphaned > orphaned.length && (
            <Button
              size="small"
              loading={loading}
              onClick={loadMore}
              data-testid="credential-health-load-more"
            >
              Load more ({totalOrphaned - orphaned.length} remaining)
            </Button>
          )}
        </Space>
      )}
      {error && <Alert type="error" showIcon message={error} style={{ borderRadius: 8 }} />}
    </Card>
  );
}

export default CredentialHealthCard;
